import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Bell, BellOff, Calendar, FileText, CreditCard, Check, ExternalLink, Volume2, VolumeX, Building2 } from "lucide-react";

const ICONOS = { calendar: Calendar, file: FileText, "credit-card": CreditCard, building: Building2 };
const COLORES = {
  amber: { bg: "#fef3c7", text: "#92400e", dot: "#f59e0b" },
  blue: { bg: "#dbeafe", text: "#1e40af", dot: "#3b82f6" },
  emerald: { bg: "#d1fae5", text: "#065f46", dot: "#10b981" },
};

const formatRelative = (iso) => {
  if (!iso) return "";
  const then = new Date(iso); const now = new Date();
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return "ahora";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} d`;
};

// Genera un beep corto usando Web Audio API (sin descargar archivos)
const playBeep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine"; o.frequency.value = 880;
    g.gain.value = 0.0001;
    g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
    o.start(); o.stop(ctx.currentTime + 0.5);
    // Segundo beep tono más alto
    setTimeout(() => {
      try {
        const o2 = ctx.createOscillator(); const g2 = ctx.createGain();
        o2.connect(g2); g2.connect(ctx.destination);
        o2.type = "sine"; o2.frequency.value = 1318;
        g2.gain.value = 0.0001;
        g2.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
        g2.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
        o2.start(); o2.stop(ctx.currentTime + 0.5);
      } catch { /* noop */ }
    }, 180);
  } catch { /* noop */ }
};

const ETIQUETAS_TIPO = {
  reserva: "Nueva reserva",
  cotizacion: "Cotización nueva",
  pago: "Pago por aprobar",
  evento_nuevo: "Evento nuevo",
  prospecto_saas: "Nuevo prospecto SaaS",
};

const showBrowserPush = (item) => {
  try {
    if (!("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    const titulo = `${ETIQUETAS_TIPO[item.tipo] || item.titulo} · CelebraMid`;
    const body = `${item.cliente}${item.monto ? ` · $${Number(item.monto).toLocaleString("es-MX")}` : ""}${item.fecha_evento ? ` · ${item.fecha_evento}` : ""}`;
    const n = new Notification(titulo, { body, tag: item.id, icon: "/favicon.ico", badge: "/favicon.ico" });
    n.onclick = () => {
      window.focus();
      if (item.accion_url) window.location.assign(item.accion_url);
      n.close();
    };
  } catch { /* noop */ }
};

const SEEN_KEY = "celebra_notif_seen_ids";
const SOUND_KEY = "celebra_notif_sound_on";

const NotificacionesBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [noLeidos, setNoLeidos] = useState(0);
  const [loading, setLoading] = useState(false);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem(SOUND_KEY) !== "false");
  const [pushAllowed, setPushAllowed] = useState(() => typeof Notification !== "undefined" && Notification.permission === "granted");
  const seenIdsRef = useRef(new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")));
  const firstLoadRef = useRef(true);
  const ref = useRef(null);

  const persistSeen = useCallback((ids) => {
    seenIdsRef.current = new Set(ids);
    try { localStorage.setItem(SEEN_KEY, JSON.stringify(ids.slice(-200))); } catch { /* noop */ }
  }, []);

  const fetchNotif = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notificaciones-actividad`);
      const lista = data.items || [];
      setItems(lista);
      setNoLeidos(data.no_leidos || 0);

      // Detectar items nuevos contra el set "ya vistos"
      const seen = seenIdsRef.current;
      const nuevos = lista.filter(it => !seen.has(it.id) && !it.leido);

      // En el PRIMER load NO disparamos sonido/push para no spamear al abrir la app.
      if (!firstLoadRef.current && nuevos.length > 0) {
        if (soundOn) playBeep();
        // push notification del navegador (sólo el más reciente)
        showBrowserPush(nuevos[0]);
      }

      const allIds = lista.map(it => it.id);
      persistSeen(allIds);
      firstLoadRef.current = false;
    } catch { /* noop */ }
  }, [soundOn, persistSeen]);

  useEffect(() => {
    fetchNotif();
    const id = setInterval(fetchNotif, 30000); // refresca cada 30s
    return () => clearInterval(id);
  }, [fetchNotif]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pedirPermisoPush = useCallback(async () => {
    if (!("Notification" in window)) return;
    try {
      const p = await Notification.requestPermission();
      setPushAllowed(p === "granted");
    } catch { /* noop */ }
  }, []);

  // Si abre el dropdown y aún no se pidió permiso, sugerirlo
  useEffect(() => {
    if (open && typeof Notification !== "undefined" && Notification.permission === "default") {
      pedirPermisoPush();
    }
  }, [open, pedirPermisoPush]);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    try { localStorage.setItem(SOUND_KEY, String(next)); } catch { /* noop */ }
    if (next) playBeep(); // sample
  };

  const marcarTodas = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/notificaciones-actividad/marcar-leidas`, { todas: true });
      await fetchNotif();
    } finally { setLoading(false); }
  };

  const abrir = async (it) => {
    try { await axios.post(`${API}/notificaciones-actividad/marcar-leidas`, { ids: [it.id] }); } catch { /* noop */ }
    setOpen(false);
    if (it.accion_url) navigate(it.accion_url);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="relative p-2 rounded-xl hover:bg-[var(--brand-surface-muted)] transition"
        data-testid="notif-bell-trigger"
        aria-label="Notificaciones"
      >
        <Bell className="w-5 h-5" style={{ color: "var(--brand-text)" }} />
        {noLeidos > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center"
            style={{ background: "#dc2626" }}
            data-testid="notif-badge"
          >
            {noLeidos > 9 ? "9+" : noLeidos}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute mt-2 w-80 sm:w-96 rounded-2xl shadow-xl border z-[200] overflow-hidden right-0 sm:right-auto sm:left-0 sm:bottom-full sm:mb-2 sm:mt-0"
          style={{ background: "var(--brand-surface-card)", borderColor: "var(--brand-border)" }}
          data-testid="notif-panel"
        >
          <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid var(--brand-border)" }}>
            <div className="min-w-0">
              <p className="font-display text-base" style={{ color: "var(--brand-text)" }}>Notificaciones</p>
              <p className="text-[11px]" style={{ color: "var(--brand-text-muted)" }}>{noLeidos} sin leer · {items.length} en total</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={toggleSound}
                className="p-1.5 rounded-lg hover:bg-[var(--brand-surface-muted)]"
                title={soundOn ? "Silenciar sonido" : "Activar sonido"}
                data-testid="notif-toggle-sound"
              >
                {soundOn ? <Volume2 className="w-4 h-4" style={{ color: "var(--brand-primary)" }} /> : <VolumeX className="w-4 h-4" style={{ color: "var(--brand-text-muted)" }} />}
              </button>
              {!pushAllowed && (
                <button
                  onClick={pedirPermisoPush}
                  className="p-1.5 rounded-lg hover:bg-[var(--brand-surface-muted)]"
                  title="Activar notificaciones del navegador"
                  data-testid="notif-pedir-push"
                >
                  <BellOff className="w-4 h-4" style={{ color: "var(--brand-text-muted)" }} />
                </button>
              )}
              {items.length > 0 && (
                <button
                  onClick={marcarTodas}
                  disabled={loading || noLeidos === 0}
                  className="text-xs font-medium hover:underline disabled:opacity-40 px-1.5"
                  style={{ color: "var(--brand-primary)" }}
                  data-testid="notif-marcar-todas"
                >
                  <Check className="w-3 h-3 inline mr-1" />Todas
                </button>
              )}
            </div>
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <Bell className="w-8 h-8 mx-auto mb-2" style={{ color: "var(--brand-text-muted)", opacity: 0.4 }} />
                <p className="text-sm" style={{ color: "var(--brand-text-muted)" }}>Sin actividad reciente</p>
                <p className="text-[11px] mt-1" style={{ color: "var(--brand-text-muted)" }}>Te avisaremos aquí cuando un cliente reserve, cotice o pague.</p>
              </div>
            ) : items.map((it) => {
              const Icon = ICONOS[it.icono] || Bell;
              const c = COLORES[it.color] || COLORES.blue;
              return (
                <button
                  key={it.id}
                  onClick={() => abrir(it)}
                  className="w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-[var(--brand-surface-muted)] transition"
                  style={{ borderBottom: "1px solid var(--brand-border)" }}
                  data-testid={`notif-item-${it.id}`}
                >
                  <div className="relative shrink-0">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: c.bg }}>
                      <Icon className="w-4 h-4" style={{ color: c.text }} />
                    </div>
                    {!it.leido && <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full" style={{ background: c.dot }} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium truncate" style={{ color: "var(--brand-text)" }}>{it.titulo}</p>
                      <span className="text-[10px] shrink-0" style={{ color: "var(--brand-text-muted)" }}>{formatRelative(it.created_at)}</span>
                    </div>
                    <p className="text-xs truncate" style={{ color: "var(--brand-text-muted)" }}>{it.cliente}{it.whatsapp ? ` · ${it.whatsapp}` : ""}</p>
                    <div className="flex items-center gap-2 mt-1 text-[11px]" style={{ color: "var(--brand-text)" }}>
                      {it.fecha_evento && <span>📅 {it.fecha_evento}</span>}
                      {it.monto > 0 && <span className="font-medium" style={{ color: c.text }}>${Number(it.monto).toLocaleString("es-MX")}</span>}
                      {it.metodo && <span className="text-stone-500">· {it.metodo}</span>}
                    </div>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 shrink-0 mt-1" style={{ color: "var(--brand-text-muted)" }} />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificacionesBell;
